"use client"

import React from "react"
import Image from "next/image"
import { useI18n } from '@/context/i18n'
import HTMLRenderer from '@/components/ui/html-renderer'
import Modal from '@/components/Modal'
import { pickLocalized } from "@/lib/i18nUtils"

type Variant = "travel" | "study" | "home" | "work" | "participaciones"

type ServiceKey = "lets-travel" | "lets-study" | "lets-inmigrate" | "lets-participaciones"
type HomeServiceKey = Exclude<ServiceKey, "lets-participaciones">

interface HeroSearchProps {
  variant?: Variant
  title?: string
  subtitle?: string
  selectedKey?: ServiceKey
  onSelect?: (key: ServiceKey) => void
  showTabs?: boolean
  imagesOverride?: string[]
  formUrl?: string
}

const defaultCopy: Record<Variant, { title: string; subtitle: string; title_en?: string; subtitle_en?: string; title_fr?: string; subtitle_fr?: string }> = {
  travel: {
    title: "Viajes Turísticos",
    subtitle: "Contamos con el mejor servicio en vuelos, hospedaje, cruceros, seguros de viaje, tours y paquetes personalizados al gusto de nuestros clientes. Representamos a las mejores empresas turísticas a nivel mundial y nacional en turismo",
    title_en: "Travel Experiences",
    subtitle_en: "We provide top service for flights, accommodations, cruises, travel insurance, tours, and tailor-made packages.",
    title_fr: "Voyages touristiques",
    subtitle_fr: "Nous offrons le meilleur service pour les vols, l'hébergement, les croisières, les assurances voyage, les circuits et des forfaits personnalisés.",
  },
  study: {
    title: "Encuentra tu programa ideal en el extranjero.",
    subtitle: "Idiomas, universidades, High School y Work & Study",
    title_en: "Find your ideal program abroad.",
    subtitle_en: "Languages, universities, high school and work & study options.",
    title_fr: "Trouvez votre programme idéal à l'étranger.",
    subtitle_fr: "Langues, universités, High School et programmes Étudier & Travailler.",
  },
  home: {
    title: "Compara y elige tu próxima experiencia.",
    subtitle: "Estudia o viaja con el respaldo de LET",
    title_en: "Compare and choose your next experience.",
    subtitle_en: "Study or travel with LET at your side.",
    title_fr: "Comparez et choisissez votre prochaine expérience.",
    subtitle_fr: "Étudiez ou voyagez avec l'accompagnement de LET.",
  },
  work: {
    title: "Impulsa tu carrera en el extranjero.",
    subtitle: "Programas co-op, prácticas y más",
    title_en: "Boost your career abroad.",
    subtitle_en: "Co-op programs, internships, and more.",
    title_fr: "Boostez votre carrière à l'étranger.",
    subtitle_fr: "Programmes coopératifs, stages et bien plus.",
  },
  participaciones: {
    title: "Nuestras participaciones destacadas.",
    subtitle: "Verifica proveedores, conectando con escuelas y proyecta tu ruta.",
    title_en: "Our featured partnerships.",
    subtitle_en: "Verify providers, connect with schools, and plan your path.",
    title_fr: "Nos participations phares.",
    subtitle_fr: "Vérifiez les fournisseurs, connectez-vous aux écoles et tracez votre parcours.",
  },
}
export default function HeroSearch({ variant = "travel", title, subtitle, selectedKey, onSelect, showTabs = true, imagesOverride, formUrl }: HeroSearchProps) {
  const { lang } = useI18n()
  const [openForm, setOpenForm] = React.useState(false)
  // Read NEXT_PUBLIC_ env directly to keep SSR/CSR consistent
  const envForm = process.env.NEXT_PUBLIC_JOTFORM_URL || ''
  const deriveFormUrl = (s?: string) => {
    if (!s) return undefined
    const trimmed = s.trim()
    if (/^https?:\/\//i.test(trimmed)) {
      try {
        const u = new URL(trimmed)
        // Convert jsform endpoint to iframe-friendly endpoint
        if (/^\/jsform\//i.test(u.pathname)) {
          const parts = u.pathname.split('/').filter(Boolean)
          const id = parts[1] // jsform/{id}
          if (id) return `${u.origin}/${id}`
        }
        return u.toString()
      } catch {
        return trimmed
      }
    }
    const m = trimmed.match(/src\s*=\s*"([^"]+)"|src\s*=\s*'([^']+)'/i)
    return m ? (m[1] || m[2]) : undefined
  }
  const jotformUrl = deriveFormUrl(formUrl) || envForm
  // Configuración de servicios para tabs e imágenes
  const services: Record<ServiceKey, { label: string; label_en?: string; label_fr?: string; title: string; title_en?: string; title_fr?: string; subtitle: string; subtitle_en?: string; subtitle_fr?: string; images: string[] }> = {
    "lets-study": {
      label: "Link Education",
      label_en: "Link Education",
      label_fr: "Link Education",
      title: "Estudios en el Extranjero",
      title_en: "Study Abroad",
      title_fr: "Études à l'étranger",
      subtitle: "Una magnífica oportunidad para formar ciudadanos del mundo: estudiantes con perspectiva global. Es un programa de inmersión lingüística, que mejora las habilidades académicas, personales. emocionales y sociales. Además de desarrollar autonomía e independencia. Permite crear vínculos significativos con personas de la misma edad, acercarse a nuevas culturas, usos costumbres: desarrollar empatía ante un mundo cada vez más diverso y globalizado",
      subtitle_en: "A great opportunity to become global citizens through language immersion programs that improve academic, personal, emotional and social skills while fostering independence.",
      subtitle_fr: "Une opportunité idéale pour devenir des citoyens du monde grâce à des programmes d'immersion linguistique qui développent les compétences académiques, personnelles, émotionnelles et sociales tout en favorisant l'autonomie.",
      images: [
        "https://drive.google.com/uc?export=view&id=1GtffIheIRuV_W1IwoaU6RFSbCl66kXTM",
        "https://drive.google.com/uc?export=view&id=1MK-OgtMa_Jy6-qJBkxJ7DeRViV8_iTOG",
        "https://drive.google.com/uc?export=view&id=1pbejRvssBx1WBbnwNTTjOHonmYE-6yWo",
        "https://drive.google.com/uc?export=view&id=1mCUQvtk2hcCIbUGaVSPZMVOWhCTIEIik",
        "https://drive.google.com/uc?export=view&id=1WoLraUnrSIlgRcJogiA6gs3DqyOSYryC",
        "https://drive.google.com/uc?export=view&id=1V3K-kKs_4N54mFQ2N1GJTn4JOHMezx5L",
        "https://drive.google.com/uc?export=view&id=1VyGWyunOMyoWgoq_A3KRd9C4dRTVnEW3",
      ],
    },
    "lets-travel": {
      label: "Link Travel",
      label_en: "Link Travel",
      label_fr: "Link Travel",
      title: "Viajes Turísticos",
      title_en: "Travel Packages",
      title_fr: "Voyages touristiques",
      subtitle: "Contamos con el mejor servicio en vuelos, hospedaje, cruceros, seguros de viaje, tours y paquetes personalizados al gusto de nuestros clientes.",
      subtitle_en: "We offer top-notch service for flights, accommodations, cruises, travel insurance, tours and custom packages tailored to our customers' preferences.",
      subtitle_fr: "Nous offrons le meilleur service pour les vols, l'hébergement, les croisières, les assurances voyage, les circuits et des forfaits sur mesure.",
      images: [
        "https://drive.google.com/uc?export=view&id=1Gjtahp39aBA_WuPaUKDuhl3hxalUhuf_",
        "https://drive.google.com/uc?export=view&id=1SPpwwbLVfpbySDZrCa0z-mdhLB1TZRsI",
        "https://drive.google.com/uc?export=view&id=13UMPoZyMTtkWHot8w3BkPOl1moBU65gy",
        "https://drive.google.com/uc?export=view&id=1UztnLMU6ui63GtHvWelBTyCyLOwi-F7V",
        "https://drive.google.com/uc?export=view&id=1YsEHOqeO7mWzZon54sMey7tLg-lcxA-h",
        "https://drive.google.com/uc?export=view&id=1_aWanAgvrxDDiSLBCra3OkwhkSNIwYLm",
      ],
    },
    "lets-inmigrate": {
      label: "Let's Inmigrate",
      label_en: "Let's Inmigrate",
      label_fr: "Let's Inmigrate",
      title: "Inmigración y residencia",
      title_en: "Immigration and Residency",
      title_fr: "Immigration et résidence",
      subtitle: "Asesoría migratoria confiable para tu proyecto: diagnóstico, ruta y acompañamiento hasta tu objetivo.",
      subtitle_en: "Trusted immigration guidance for your journey: assessment, roadmap and support through to your goal.",
      subtitle_fr: "Conseil migratoire fiable pour votre projet : diagnostic, feuille de route et accompagnement jusqu'à l'objectif.",
      images: [
        "/hero-images/study-4.jpg",
        "/hero-images/study-6.jpg",
        "/hero-images/study-2.webp",
        "/hero-images/travel-5.jpg",
        "/hero-images/study-7.jpg",
        "/hero-images/travel-3.jpg",
      ],
    },
    "lets-participaciones": {
      label: "Participaciones",
      label_en: "Participations",
      label_fr: "Participations",
      title: "Somos partes de nuevas experiencias",
      title_en: "Our Featured Participations",
      title_fr: "Nous faisons partie de nouvelles expériences",
      subtitle: "Seguros, visas, alojamiento, traslados y soporte integral",
      subtitle_en: "Insurance, visas, accommodation, transfers and comprehensive support",
      subtitle_fr: "Assurances, visas, hébergement, transferts et accompagnement complet",
      images: [
        "https://www.grupo-let.com/_next/image?url=https%3A%2F%2Fwww.grupo-let.com%2F_next%2Fimage%3Furl%3Dhttps%253A%252F%252Fdrive.google.com%252Fuc%253Fexport%253Dview%2526id%253D1eh1AMa-U13uLPq3fU0rZqXiQuHAXpxU4%26w%3D384%26q%3D70&w=640&q=70",
        "https://yt3.googleusercontent.com/1GbRfJLbdBIUdNVif7Sow5mn6Y9VTVPKvC--aaAf7chPr02nDxZulPhevd57ViT3qLElCiWf8g=s900-c-k-c0x00ffffff-no-rj",
        "https://amdetur.org.mx/wp-content/uploads/2024/03/Verifica-y-Viaja-ACLUVAQ.jpeg",
        "https://www.grupo-let.com/_next/image?url=https%3A%2F%2Fwww.grupo-let.com%2F_next%2Fimage%3Furl%3Dhttps%253A%252F%252Fdrive.google.com%252Fuc%253Fexport%253Dview%2526id%253D1eKrhlFKIjuOSKjPW4weVD8VTD3MiASg4%26w%3D384%26q%3D70&w=640&q=70",
      ],
    },

  }

  const defaultKey: ServiceKey =
    variant === "study" ? "lets-study" :
      variant === "travel" ? "lets-travel" :
        variant === "participaciones" ? "lets-participaciones" :
          "lets-study"
  const homeServiceKeys = (Object.keys(services) as ServiceKey[]).filter(
    (key): key is HomeServiceKey => key !== "lets-participaciones" && key !== "lets-inmigrate"
  )
  const isHomeKey = (key: ServiceKey): key is HomeServiceKey =>
    key !== "lets-participaciones" && key !== "lets-inmigrate"
  const requestedKey = selectedKey ?? defaultKey
  let activeKey: ServiceKey
  if (variant === "home") {
    // If inmigrate is explicitly requested, respect it and do not coerce to home keys
    if (requestedKey === "lets-inmigrate") {
      activeKey = "lets-inmigrate"
    } else if (isHomeKey(requestedKey)) {
      activeKey = requestedKey
    } else {
      activeKey = homeServiceKeys[0] ?? defaultKey
    }
  } else {
    activeKey = requestedKey
  }
  const fallbackSvc = services["lets-participaciones"] ?? {
    label: "Participaciones",
    title: (defaultCopy as any)[variant]?.title ?? "",
    subtitle: (defaultCopy as any)[variant]?.subtitle ?? "",
    images: [],
  }
  const svc = services[activeKey] ?? fallbackSvc
  const imagesToUse = (imagesOverride && imagesOverride.length ? imagesOverride : svc.images)

  const finalTitle = title ?? pickLocalized(lang, svc.title, svc.title_en, svc.title_fr)
  const finalSubtitle = subtitle ?? pickLocalized(lang, svc.subtitle, svc.subtitle_en, svc.subtitle_fr)

  const highlightByVariant: Record<Variant, string[]> = {
    travel: ["Viajes", "vuelos", "paquetes", "Turísticos"],
    study: ["programa", "extranjero", "idiomas"],
    home: ["Experiencia", "elige"],
    work: ["carrera", "co-op"],
    participaciones: ["destacadas"],

  }
  function highlightText(text: string, words: string[]) {
    const tokens = text.split(/(\s+)/)
    const set = new Set(words.map(w => w.toLowerCase()))
    return tokens.map((t, i) => {
      const plain = t.replace(/[^\p{L}\p{N}]+/gu, "").toLowerCase()
      if (set.has(plain)) {
        return <span key={i} className="text-[#fb0009]">{t}</span>
      }
      return <span key={i}>{t}</span>
    })
  }

  const listKeys: ServiceKey[] =
    // When viewing inmigrate within home variant, don't show other tabs
    variant === "home"
      ? (requestedKey === "lets-inmigrate" ? [activeKey] : homeServiceKeys)
      : [activeKey]
  const tabs = listKeys
    .filter((k) => Boolean(services[k]))
    .map((k) => ({
      key: k,
      label: pickLocalized(lang, services[k].label, services[k].label_en, services[k].label_fr),
      active: k === activeKey,
    }))

  const shouldRenderTabs = (variant === "home") && showTabs && tabs.length > 1

  const handleSelect = React.useCallback(
    (key: ServiceKey) => {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent('home-tour:select-category', { detail: { key } }))
      }
      onSelect?.(key)
    },
    [onSelect],
  )

  return (
    <section id="hero" className="relative bg-white">
      <div className="container mx-auto px-4 md:px-8 py-10 md:py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            {/* Reserve space for title to mitigate CLS: fixed min-height approximates 2-3 lines across breakpoints */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black leading-tight text-darkmb-3 min-h-[3.5rem] sm:min-h-[4.5rem] md:min-h-[5.5rem]">
              {highlightText(finalTitle, highlightByVariant[variant])}
            </h1>
            <br />
            {/* Subtitle container given a min-height to reserve layout before translations/content arrive */}
            <div className="text-gray-600 text-base md:text-lg mb-6 min-h-[4.5rem] md:min-h-[3.5rem]">
              <HTMLRenderer content={finalSubtitle} />
            </div>

            {/* CTA: Comienza tu registro aquí (usa URL por servicio si está configurada) */}
            {variant !== 'home' && (
              <div className="mb-6">
                <button
                  onClick={() => setOpenForm(true)}
                  className="inline-flex items-center gap-2 bg-[#fb0009] text-white font-bold px-5 py-3 rounded-lg shadow hover:bg-black transition-colors"
                >
                  {pickLocalized(lang, 'Comienza tu registro aquí', 'Start your registration here', 'Commencez votre inscription ici')}
                </button>
                <a
                  href="/mapa-destinos"
                  target="_blank"
                  className="ml-4 inline-flex items-center gap-2 bg-gray-200 text-gray-800 font-bold px-5 py-3 rounded-lg shadow hover:bg-black hover:text-white transition-colors"
                >
                  {pickLocalized(lang, 'Conoce nuestros destinos', 'Discover our destinations', 'Découvrez nos destinations')}
                </a>
              </div>
            )}

            {shouldRenderTabs && (
              <div className="relative" data-tour="service-tabs">
                <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-6">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => handleSelect(tab.key as ServiceKey)}
                      className={`flex items-center gap-2 bg-white px-4 py-3 border border-gray-200 shadow-sm ${tab.active ? 'ring-2 ring-[#fb0009]/50' : 'hover:shadow-md'}`}
                    >
                      <span className={`font-semibold ${tab.active ? 'text-gray-900' : 'text-gray-700'}`}>{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Buscador removido */}

            {/* Caja de búsqueda */}
            <div className="hidden">
              {activeKey === 'lets-travel' ? (
                <>
                  <Field label="Origen" value="Ciudad de México (MEX)" strong />
                  <Separator />
                  <Field label="Destino" placeholder="Destino" />
                  <Separator hiddenOnMobile />
                  <Field label="Salida – Regreso" placeholder="Selecciona fechas" />
                  <Separator hiddenOnMobile />
                  <Field label="Pasajeros" placeholder="1 pasajero, Económica" />
                </>
              ) : (
                <>
                  <Field label="País / Ciudad" placeholder="Ej. Canadá, Vancouver" />
                  <Separator />
                  <Field label="Programa" placeholder="Idiomas, College, HS..." />
                  <Separator hiddenOnMobile />
                  <Field label="Fecha de inicio" placeholder="Selecciona fecha" />
                  <Separator hiddenOnMobile />
                  <Field label="Duración" placeholder="2 - 52 semanas" />
                </>
              )}
              <button className="shrink-0 h-12 w-12 bg-[#fb0009] text-white grid place-items-center hover:bg-black transition-colors" aria-label="Buscar">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79L20 21.5 21.5 20 15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" /></svg>
              </button>
            </div>
          </div>

          {/* Mosaico de imágenes: montado diferido para mejorar Speed Index y evitar CLS */}
          <div className="hidden lg:block">
            <LazyHeroMarquee images={imagesToUse} />
          </div>
        </div>
      </div>
      {/* Modal con JotForm */}
      <Modal open={openForm} onClose={() => setOpenForm(false)}>
        <div className="w-full max-h-[85vh] overflow-auto">
          {jotformUrl ? (
            <iframe
              src={jotformUrl}
              className="w-full h-[70vh] md:h-[75vh] lg:h-[80vh]"
              title="Registro"
              allow="camera; microphone; clipboard-read; clipboard-write"
            />
          ) : (
            <div className="p-6">
              <p className="text-black">Configura NEXT_PUBLIC_JOTFORM_URL para incrustar el formulario.</p>
            </div>
          )}
        </div>
      </Modal>
    </section>
  )
}

function Field({ label, value, placeholder, strong = false }: { label: string; value?: string; placeholder?: string; strong?: boolean }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="text-xs text-gray-500">{label}</div>
      {value ? (
        <div className="text-gray-900 font-medium truncate">{value}</div>
      ) : (
        <div className={`truncate ${strong ? 'text-gray-900' : 'text-gray-400'}`}>{placeholder}</div>
      )}
    </div>
  )
}

function Separator({ hiddenOnMobile = false }: { hiddenOnMobile?: boolean }) {
  return <div className={`${hiddenOnMobile ? 'hidden md:block' : ''} w-px bg-gray-200`} />
}

function VerticalMarquee({ images }: { images: string[] }) {
  const left = images.filter((_, i) => i % 2 === 0)
  const right = images.filter((_, i) => i % 2 === 1)
  return (
    <div className="grid grid-cols-2 gap-4 h-[460px] md:h-[520px] overflow-hidden">
      <MarqueeColumn images={left} speed={22} />
      <MarqueeColumn images={right} speed={28} reverse />
    </div>
  )
}

function MarqueeColumn({ images, speed = 24, reverse = false }: { images: string[]; speed?: number; reverse?: boolean }) {
  const seq = images.length ? images : [
    "https://images.unsplash.com/photo-1548588689-31676504c3d9?q=80&w=1600&auto=format&fit=crop",
  ]
  // Only duplicate once instead of concatenating many times
  const doubled = seq.concat(seq)
  return (
    <div className="relative overflow-hidden">
      <div
        className="flex flex-col gap-4"
        style={{
          animation: `marqueeY ${speed}s linear infinite`,
          animationDirection: reverse ? 'reverse' : 'normal',
          willChange: 'transform',
          transform: 'translateZ(0)', // Force GPU layer
        }}
      >
        {doubled.map((src, i) => {
          // Patrón de alturas: pequeño, alto, medio (desfasado si es la columna derecha)
          const sizeOptions = [
            "h-38 md:h-48 lg:h-48", // pequeño
            "h-46 md:h-52 lg:h-52", // alto
            "h-52 md:h-60 lg:h-68", // medio
          ]
          const offset = reverse ? 1 : 0
          const size = sizeOptions[(i % sizeOptions.length + offset) % sizeOptions.length]
          return (
            <div key={i} className={`rounded-3xl overflow-hidden ${size} relative`}>
              <Image
                src={src}
                alt={`Imagen ${i + 1}`}
                fill
                className="object-cover"
                loading="lazy"
                sizes="(max-width: 768px) 50vw, 25vw"
              />
            </div>
          )
        })}
      </div>
      <style jsx>{`
        @keyframes marqueeY {
          0% { transform: translateY(0) translateZ(0); }
          100% { transform: translateY(-50%) translateZ(0); }
        }
      `}</style>
    </div>
  )
}

// Cliente: monta el marquee tras idle/interacción para reducir trabajo inicial.
function LazyHeroMarquee({ images }: { images: string[] }) {
  const [ready, setReady] = React.useState(false)

  React.useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    const trigger = () => setReady(true)
    const onLoad = () => {
      if ('requestIdleCallback' in window) {
        ; (window as any).requestIdleCallback(() => {
          timeout = setTimeout(trigger, 150)
        }, { timeout: 1000 })
      } else {
        timeout = setTimeout(trigger, 700)
      }
    }
    // Espera a que termine el evento load para reducir el impacto visual inicial
    if (document.readyState === 'complete') {
      onLoad()
    } else {
      window.addEventListener('load', onLoad)
    }
    return () => {
      window.removeEventListener('load', onLoad)
      if (timeout) clearTimeout(timeout)
    }
  }, [])

  if (ready) return <VerticalMarquee images={images} />

  // Placeholder que replica estructura final para evitar reflow al intercambiar (mismos heights y grid)
  const sizeOptions = [
    'h-38 md:h-48 lg:h-48',
    'h-46 md:h-52 lg:h-52',
    'h-52 md:h-60 lg:h-68'
  ]
  const cells: React.ReactElement[] = []
  // Simular al menos 6 elementos (tamaño razonable) alternando alturas como versión final
  for (let i = 0; i < 6; i++) {
    const size = sizeOptions[i % sizeOptions.length]
    cells.push(
      <div key={i} className={`rounded-3xl overflow-hidden ${size} relative bg-gray-100 animate-pulse`} />
    )
  }
  const left = cells.filter((_, i) => i % 2 === 0)
  const right = cells.filter((_, i) => i % 2 === 1)
  return (
    <div className="grid grid-cols-2 gap-4 h-[460px] md:h-[520px] overflow-hidden">
      <div className="flex flex-col gap-4">
        {left}
      </div>
      <div className="flex flex-col gap-4">
        {right}
      </div>
    </div>
  )
}
